import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import axios from 'axios';
import { API_BASE } from '../config/env';
import { useAuth } from './AuthContext';

/**
 * QueueContext
 * ─────────────────────────────────────────────────────────────────────────
 * Maintains ONE global SSE connection synced to the user's authentication state.
 * Any component can:
 *   1. Read  queueStatuses  – { [request_number]: 'PENDING'|'PROCESSING' }
 *   2. Call  applyUpdate(request_number, status) – Optimistically mark status
 *   3. Call  subscribeCompleted(fn) / subscribeFailed(fn)  to register a
 *      listener that fires on queue:completed / queue:failed events.
 *      The listener is automatically unregistered when the caller unmounts.
 */

const QueueContext = createContext(null);

export function QueueProvider({ children }) {
  const { token, isAuthenticated } = useAuth();
  const [queueStatuses, setQueueStatuses] = useState({});
  // Incremented every time a job completes/fails so dashboard tables know to re-fetch
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);

  // Sets of listener functions registered by mounted components
  const completedListeners = useRef(new Set());
  const failedListeners    = useRef(new Set());

  // ── Helper: update queueStatuses map ────────────────────────────────────
  const applyUpdate = useCallback((request_number, status) => {
    if (!request_number) return;
    const key = String(request_number);
    setQueueStatuses((prev) => {
      if (status === 'COMPLETED' || status === 'FAILED') {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      if (prev[key] === status) return prev;
      return { ...prev, [key]: status };
    });
  }, []);

  // ── Global SSE connection & initial snapshot synced to auth session ─────
  useEffect(() => {
    if (!token || !isAuthenticated) {
      setQueueStatuses({});
      return;
    }

    let destroyed = false;
    let es = null;
    let reconnectTimer = null;

    // Fetch initial snapshot for active queue jobs
    const fetchSnapshot = async () => {
      try {
        const res = await axios.get(`${API_BASE}/queueStatus`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (destroyed) return;
        const map = {};
        for (const item of res.data || []) {
          if (item?.request_no) {
            map[String(item.request_no)] = item.status;
          }
        }
        setQueueStatuses(map);
      } catch (err) {
        if (!destroyed) {
          console.warn('[QueueContext] Snapshot fetch failed:', err.message);
        }
      }
    };

    fetchSnapshot();

    // Connect SSE stream
    const connect = () => {
      if (destroyed) return;

      const url = `${API_BASE}/queueStream?token=${encodeURIComponent(token)}`;
      es = new EventSource(url);

      const handleStatusUpdate = (e) => {
        try {
          const data = JSON.parse(e.data);
          applyUpdate(data.request_number, data.status);
        } catch (err) {
          console.error('[QueueContext] SSE parse error:', err);
        }
      };

      const handleCompleted = (e) => {
        try {
          const data = JSON.parse(e.data);
          applyUpdate(data.request_number, 'COMPLETED');
          completedListeners.current.forEach((fn) => fn(data));
          setDashboardRefreshKey((k) => k + 1);
        } catch (err) {
          console.error('[QueueContext] SSE completed parse error:', err);
        }
      };

      const handleFailed = (e) => {
        try {
          const data = JSON.parse(e.data);
          applyUpdate(data.request_number, 'FAILED');
          failedListeners.current.forEach((fn) => fn(data));
          setDashboardRefreshKey((k) => k + 1);
        } catch (err) {
          console.error('[QueueContext] SSE failed parse error:', err);
        }
      };

      es.addEventListener('queue:added',      handleStatusUpdate);
      es.addEventListener('queue:processing', handleStatusUpdate);
      es.addEventListener('queue:completed',  handleCompleted);
      es.addEventListener('queue:failed',     handleFailed);

      es.onerror = () => {
        if (destroyed) return;
        console.warn('[QueueContext] SSE disconnected — reconnecting in 5 s…');
        es.close();
        reconnectTimer = setTimeout(() => {
          fetchSnapshot();
          connect();
        }, 5000);
      };
    };

    connect();

    return () => {
      destroyed = true;
      clearTimeout(reconnectTimer);
      if (es) {
        es.close();
      }
    };
  }, [token, isAuthenticated, applyUpdate]);

  // ── Public API ───────────────────────────────────────────────────────────
  /**
   * Register a listener for queue:completed events.
   * Returns an unsubscribe function — call it in useEffect cleanup.
   */
  const subscribeCompleted = useCallback((fn) => {
    completedListeners.current.add(fn);
    return () => completedListeners.current.delete(fn);
  }, []);

  /**
   * Register a listener for queue:failed events.
   * Returns an unsubscribe function — call it in useEffect cleanup.
   */
  const subscribeFailed = useCallback((fn) => {
    failedListeners.current.add(fn);
    return () => failedListeners.current.delete(fn);
  }, []);

  return (
    <QueueContext.Provider
      value={{
        queueStatuses,
        applyUpdate,
        subscribeCompleted,
        subscribeFailed,
        dashboardRefreshKey,
      }}
    >
      {children}
    </QueueContext.Provider>
  );
}

export function useQueue() {
  const ctx = useContext(QueueContext);
  if (!ctx) throw new Error('useQueue must be used inside <QueueProvider>');
  return ctx;
}
